import { ALERTLESS_KIT_STATUS_TITLES, KIT_DETAIL_ALERTS, KIT_STATUS_TITLES, NEEDS_ACTIVATION_KIT_STATUS_TITLES, getKitStatusQuery, parseKitDetailAlert, parseKitStatus, } from './kitStatus';
/**
 * Translates one frontend kit status title (e.g. `'Void'`, `'Ready'`)
 * into an AND of leaf clauses by reading {@link getKitStatusQuery}.
 */
export function kitStatusTitleToClause(title) {
    const query = getKitStatusQuery(title);
    const clauses = [];
    // status is undefined for date-driven rules and an array (possibly empty)
    // for parsed-title fallbacks. Emit even when empty so unknown titles
    // produce a never-matches clause instead of vacuously matching all.
    if (query.status !== undefined) {
        clauses.push({ field: 'status', op: 'in', expr: query.status });
    }
    for (const field of query.null_forbidden_fields ?? []) {
        clauses.push({ field, op: 'is not', expr: null });
    }
    for (const field of query.null_required_fields ?? []) {
        clauses.push({ field, op: 'is', expr: null });
    }
    return { and: clauses };
}
/** Statuses whose Details cell can show an alert at all. */
const ALERT_CAPABLE_TITLES = KIT_STATUS_TITLES.filter(title => !ALERTLESS_KIT_STATUS_TITLES.includes(title));
/**
 * DB-side counterpart of {@link parseKitDetailAlert}: the predicate selecting
 * kits whose Details cell displays `alert`.
 */
export function kitDetailAlertToClause(alert) {
    if (alert === 'Need to activate kit') {
        return {
            or: NEEDS_ACTIVATION_KIT_STATUS_TITLES.map(title => ({
                and: [
                    kitStatusTitleToClause(title),
                    { field: 'date_activated', op: 'is', expr: null },
                ],
            })),
        };
    }
    // Mirrors hasIncompleteSurvey. Requiring an activation date also subsumes
    // the "Need to activate kit" branch above, which only claims kits without
    // one, so the two alerts stay mutually exclusive without a special case.
    return {
        and: [
            { or: ALERT_CAPABLE_TITLES.map(kitStatusTitleToClause) },
            { field: 'date_activated', op: 'is not', expr: null },
            { field: 'date_survey_completed', op: 'is', expr: null },
        ],
    };
}
/** The three cards of the portal home page Summary section, in display order. */
export const KIT_SUMMARY_CARDS = [
    'needsAttention',
    'resultsReady',
    'releasedLast30Days',
];
/** Rolling window for the "Results released in last 30 days" card. */
export const RESULTS_RELEASED_WINDOW_DAYS = 30;
export function resultsReleasedCutoff(now) {
    return new Date(now.getTime() - RESULTS_RELEASED_WINDOW_DAYS * 24 * 60 * 60 * 1000);
}
/**
 * Whether a kit belongs to `card`, evaluated against the same parsed title and
 * Details alert the table renders. Client-side counterpart of
 * {@link getKitSummaryCardClause} — the two must agree, so change them together.
 */
export function matchesKitSummaryCard(card, kit, now = new Date()) {
    switch (card) {
        case 'needsAttention':
            return parseKitDetailAlert(kit) != null;
        case 'resultsReady':
            return parseKitStatus(kit).title === 'Practitioner review';
        case 'releasedLast30Days':
            return (parseKitStatus(kit).title === 'Ready' &&
                kit.date_results_ready != null &&
                new Date(kit.date_results_ready) >= resultsReleasedCutoff(now));
    }
}
/**
 * DB-side predicate selecting the kits `card` counts. Consumed both by the
 * partner summary endpoint and by the kits table when a card filter is active,
 * so a card's count and its filtered rows can never drift apart.
 */
export function getKitSummaryCardClause(card, now = new Date()) {
    switch (card) {
        case 'needsAttention':
            return { or: KIT_DETAIL_ALERTS.map(kitDetailAlertToClause) };
        case 'resultsReady':
            return kitStatusTitleToClause('Practitioner review');
        case 'releasedLast30Days':
            return {
                and: [
                    kitStatusTitleToClause('Ready'),
                    {
                        field: 'date_results_ready',
                        op: '>=',
                        expr: resultsReleasedCutoff(now).toISOString(),
                    },
                ],
            };
    }
}
/** Counts every card across `kits`, for portals holding the full dataset. */
export function countKitSummary(kits, now = new Date()) {
    const counts = {
        needsAttention: 0,
        resultsReady: 0,
        releasedLast30Days: 0,
    };
    for (const kit of kits) {
        for (const card of KIT_SUMMARY_CARDS) {
            if (matchesKitSummaryCard(card, kit, now))
                counts[card] += 1;
        }
    }
    return counts;
}
//# sourceMappingURL=kitSummary.js.map